/**
 * Once this file is deleted, we should do the following:
 * - remove the conditional LogoutLinkWrapper block from Header.tsx
 * - remove associated setLookerDashboardOpened from the Redux store
 */

import styled from '@emotion/styled';
import React, { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { useDispatch, useSelector } from 'react-redux';

import { adminService, toastNotificationsService } from 'src/config/rootService';
import { Dispatch } from 'src/config/rootStore';
import { DashboardCard } from 'src/modules/admin/components/DashboardCard';
import { PdfDocument } from 'src/modules/admin/components/PdfDocument';
import { config, config as adminConfig } from 'src/modules/admin/config/config';
import { isLookerDashboardIframeVisibleSelector } from 'src/modules/admin/selectors/adminSelectors';
import { getUserInformation } from 'src/modules/user/selectors/userSelectors';
import closeIcon from 'src/public/img/icon-close-circle.svg';
import { Banner } from 'src/shared/components/Banner';
import { Modal } from 'src/shared/components/Modal';
import { color, font, layout } from 'src/shared/styles/variables';

import { useFlags } from 'launchdarkly-react-client-sdk';
import { AdminTerms } from 'src/modules/admin/components/AdminTerms';
import { Password } from 'src/modules/admin/components/Password';
import bannerImg from '../assets/banner.svg';
import contactImg from '../assets/contact.svg';
import reportsImage from '../assets/reports.svg';
import resourcesImage from '../assets/resource.svg';
import { LATEST_TOU_VERSION } from '../components/AdminTerms';

const DashboardWrapper = styled.div({
    backgroundColor: color.white,
    flexGrow: 1,
});

const DashboardContent = styled.div({
    maxWidth: layout.containerWidth,
    margin: '0 auto',
    padding: '1rem 1.5rem',

    '.banner__wrapper': {
        backgroundImage: `url(${bannerImg})`,
        marginBottom: 40,
    },

    '.banner__contentWrapper': {
        width: '50%',
    },

    '.banner__description': {
        fontSize: font.size.mediumExtended,
        fontWeight: 500,
    },
});

const DashboardRow = styled.div({
    display: 'flex',
    flexDirection: 'row',
    '.adminDashboardCard+.adminDashboardCard': {
        marginLeft: '15px',
    },
});

const CardImage = styled.img({
    position: 'absolute',
    bottom: 0,
    right: -15,
});

const Footer = styled.div({
    display: 'flex',
    justifyContent: 'center',
    marginTop: 53,
    whiteSpace: 'pre',
});

const FooterIcon = styled.img({
    marginRight: 13,
});

const ReportingImage = styled(CardImage)({
    bottom: '-2px',
});

const Link = styled.a(
    {
        color: color.royalBlue,
        textDecoration: 'underline',
    },
    font.heavy(font.size.base),
);

const LookerDashboard = styled.iframe({
    display: 'flex',
    flex: 1,
});

const ResourceContainer = styled.div({
    display: 'flex',
    flexDirection: 'column',
});

const PasswordContainer = styled.strong({
    display: 'flex',
    marginTop: 12,
});

const PdfModal = styled(Modal)({
    '&.ant-modal': {
        height: '90%',
    },
    '.ant-modal-content': {
        height: '90%',
        maxHeight: '90%',
        overflowY: 'scroll',
        borderRadius: '16px',
        scrollWidth: 0,
        position: 'static',
    },
    '.ant-modal-content::-webkit-scrollbar': {
        width: 0,
    },
});

export enum AdminDashboardPageTestId {
    lookerDashboard = 'AdminDashboardPageTestId:lookerDashboard',
}

export const LegacyAdminDashboardPage = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch<Dispatch>();
    const user = useSelector(getUserInformation);
    const { adminDashboardUseIframe } = useFlags();
    const isLookerDashboardOpened = useSelector(isLookerDashboardIframeVisibleSelector);
    const [isGlossaryModalVisible, setGlossaryModalVisible] = useState(false);
    const [lookerLink, setLookerLink] = useState<string | null>(null);
    const acceptedAdminTerms = localStorage.getItem(adminConfig.termsLocalStorageKey);

    if (acceptedAdminTerms === null || acceptedAdminTerms !== LATEST_TOU_VERSION) {
        return <AdminTerms />;
    }

    const handleReportingOpen = async () => {
        const lookerSSOLink = await adminService.fetchLookerSSO();

        if (!lookerSSOLink) {
            toastNotificationsService.renderErrorToast(t('admin:looker.general_error'));
            return;
        }
        if (!lookerSSOLink.url) {
            toastNotificationsService.renderErrorToast(
                lookerSSOLink?.error || t('admin:looker.general_error'),
            );
            return;
        }

        if (adminDashboardUseIframe) {
            setLookerLink(null);

            if (lookerSSOLink.url) {
                setLookerLink(lookerSSOLink.url);
                dispatch.admin.setLookerDashboardOpened(true);
            }
            return;
        }

        return window.open(lookerSSOLink.url, '_blank');
    };

    if (isLookerDashboardOpened && lookerLink) {
        return (
            <LookerDashboard
                src={lookerLink ?? undefined}
                data-testid={AdminDashboardPageTestId.lookerDashboard}
            />
        );
    }

    return (
        <>
            <DashboardWrapper>
                <DashboardContent>
                    <Banner
                        title={t('admin:dashboard.banner.title', {
                            name: user.firstName,
                            company: user.company,
                        })}
                        description={t('admin:dashboard.banner.description')}
                    />
                    <DashboardRow>
                        <DashboardCard
                            title={t('admin:dashboard.reporting.title')}
                            description={
                                <Trans
                                    t={t}
                                    ns={'admin'}
                                    i18nKey="dashboard.reporting.description"
                                    components={[
                                        // eslint-disable-next-line react/jsx-key
                                        <Link onClick={() => setGlossaryModalVisible(true)} />,
                                    ]}
                                />
                            }
                            buttonText={t('admin:dashboard.reporting.action')}
                            image={
                                <ReportingImage src={reportsImage} alt="admin portal reporting" />
                            }
                            onClick={handleReportingOpen}
                        />
                        <DashboardCard
                            title={t('admin:dashboard.resource.title')}
                            description={
                                <ResourceContainer>
                                    {t('admin:dashboard.resource.description')}
                                    <PasswordContainer>
                                        <Trans
                                            t={t}
                                            ns={'admin'}
                                            i18nKey="dashboard.resource.password"
                                            components={[
                                                <Password
                                                    key="dashboard.resource.description_0"
                                                    password={config.clientPortalPassword}
                                                />,
                                            ]}
                                        />
                                    </PasswordContainer>
                                </ResourceContainer>
                            }
                            buttonText={t('admin:dashboard.resource.action')}
                            image={<CardImage src={resourcesImage} alt="admin portal resources" />}
                            onClick={() => window.open(config.clientPortalUrl, '_blank')}
                        />
                    </DashboardRow>
                    <Footer>
                        <FooterIcon src={contactImg} alt="icon" />
                        <Trans
                            t={t}
                            ns={'admin'}
                            i18nKey="dashboard.footer"
                            // eslint-disable-next-line react/jsx-key
                            components={[<Link href={`mailto:${config.csmMail}`} />]}
                        />
                    </Footer>
                </DashboardContent>
            </DashboardWrapper>
            <PdfModal
                visible={isGlossaryModalVisible}
                footer={null}
                width="90%"
                onCancel={() => setGlossaryModalVisible(false)}
                closeIcon={<img src={closeIcon} alt="close icon" />}
            >
                <PdfDocument file={config.glossaryPdfUrl} />
            </PdfModal>
        </>
    );
};
